import {
  DEFAULT_YAK_TRAILS_CONFIG,
  DEFAULT_YAK_TRAILS_CONFIG_HASH,
  YAK_TRAILS_PROTOCOL_VERSION,
  type RoomConnection,
  type RoomDisconnectReason,
  type RoomEvent,
  type SteeringInput,
  type TeamRules,
} from "@games-monorepo/yak-trails-core";

import { YAK_TRAILS_CLIENT_TUNING } from "../tuning";
import { BaseRoomTransport } from "./BaseRoomTransport";

export interface LocalPracticeOptions {
  botCount: number;
  skinId: string;
  resume?: boolean;
  workerUrl?: string;
  startupTimeoutMs?: number;
  mode?: "ffa" | "team";
  teamRules?: TeamRules;
}

type WorkerOutbound =
  | { type: "room-event"; event: RoomEvent }
  | { type: "ready" };

export class LocalPracticeTransport extends BaseRoomTransport {
  private worker: Worker | null = null;
  private connecting: Promise<void> | null = null;
  private cancelPendingConnection: ((error: Error) => void) | null = null;

  constructor(private readonly options: LocalPracticeOptions) {
    super();
  }

  static connection(): RoomConnection {
    return {
      roomId: "local-practice",
      socketUrl: "local://practice",
      ticket: "local",
      protocolVersion: YAK_TRAILS_PROTOCOL_VERSION,
      rulesetVersion: DEFAULT_YAK_TRAILS_CONFIG.rulesetVersion,
      configVersion: DEFAULT_YAK_TRAILS_CONFIG.configVersion,
      configHash: DEFAULT_YAK_TRAILS_CONFIG_HASH,
    };
  }

  async connect(_connection: RoomConnection): Promise<void> {
    if (this.connecting) return this.connecting;
    if (this.worker) return;
    const worker = this.options.workerUrl
      ? new Worker(this.options.workerUrl, { type: "module" })
      : new Worker(new URL("../workers/practice.worker.ts", import.meta.url), { type: "module" });
    this.worker = worker;
    this.connecting = new Promise<void>((resolve, reject) => {
      let settled = false;
      const settle = (error?: Error) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timeout);
        this.cancelPendingConnection = null;
        if (error) reject(error);
        else resolve();
      };
      this.cancelPendingConnection = (error) => settle(error);
      const timeout = globalThis.setTimeout(() => {
        settle(new Error("Practice took too long to start. Please try again."));
      }, this.options.startupTimeoutMs ?? YAK_TRAILS_CLIENT_TUNING.startup.practiceWorkerStartupTimeoutMs);
      worker.onmessage = (message: MessageEvent<WorkerOutbound>) => {
        if (message.data.type === "ready") settle();
        else {
          this.emit(message.data.event);
          if (message.data.event.type === "snapshot") settle();
        }
      };
      worker.onerror = (event) => {
        event.preventDefault();
        const error = new Error("Practice mode could not start. Please try again.");
        this.emit({ type: "error", code: "practice_worker_failed", message: error.message, retryable: true });
        settle(error);
      };
      worker.postMessage({ type: "start", ...this.options });
    });
    try {
      await this.connecting;
    } catch (error) {
      worker.terminate();
      if (this.worker === worker) this.worker = null;
      throw error;
    } finally {
      this.connecting = null;
    }
  }

  sendSteering(input: SteeringInput): void {
    this.worker?.postMessage({ type: "input", input });
  }

  requestRespawn(): void {
    this.worker?.postMessage({ type: "respawn" });
  }

  requestResync(): void {
    this.worker?.postMessage({ type: "resync" });
  }

  pause(): void {
    this.worker?.postMessage({ type: "pause" });
  }

  resume(): void {
    this.worker?.postMessage({ type: "resume" });
  }

  async disconnect(_reason: RoomDisconnectReason): Promise<void> {
    this.cancelPendingConnection?.(new Error("Practice startup was cancelled."));
    this.worker?.postMessage({ type: "stop" });
    this.worker?.terminate();
    this.worker = null;
    this.connecting = null;
  }
}
